import { useCallback } from 'react';
import { PreferenceContextEnum, TasksPersonalPreferences } from '@scrambl/shared';
import { useUserPreferencesRealtime } from '@/lib/realtime/features/useUserPreferencesRealtime';
import { useMyTasksStore } from './store';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useMyTasksRealtime');

/**
 * Hook to subscribe to realtime updates for My Tasks preferences.
 * Use this in any component that displays My Tasks data to keep it in sync
 * across windows/tabs/devices.
 */
export function useMyTasksRealtime() {
  // Use getState() to access the action directly without creating a subscription hook
  // This avoids the hook ordering issue that can occur with createSelectors
  const handlePreferencesUpdated = useCallback((data: {
    context: PreferenceContextEnum;
    preferences: Record<string, unknown>;
  }) => {
    logger.info('[useMyTasksRealtime] Received preferences update:', data.context);

    // The preferences come as a generic Record, cast to TasksPersonalPreferences
    const preferences = data.preferences as unknown as TasksPersonalPreferences;

    // Apply the realtime update to the store using getState() to get the action
    useMyTasksStore.getState().setPreferencesFromRealtime(preferences);
  }, []);

  const { isReady } = useUserPreferencesRealtime({
    context: PreferenceContextEnum.TASKS_PERSONAL,
    onPreferencesUpdated: handlePreferencesUpdated,
  });

  return { isReady };
}
